import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Brand } from '../../components/Brand';
import { Appearance } from '../../components/Appearance';
import { Help } from '../../components/Help';
import { BUILD_VERSION } from '../../../server/generated/build-info';
import { LegalFooter } from '../../components/LegalFooter';
import {
  ArtCalendar,
  ArtCustomize,
  ArtShare,
  FeatureCard,
  IconCalendar,
  IconChart,
  IconChat,
  IconPeople,
  StepFlow,
  type FeatureContent,
  type StepContent,
} from '../components/HomeSections';
import { formatPrice, type PlanPublic } from '@shared/entitlements';
import { monthlyTotal } from '@shared/pricing';

interface PublicPricing {
  included_sms_credits: number;
  credit_package_price_cents: number;
  credit_package_credits: number;
  marketing_price_cents: number;
}

interface DemoService {
  name: string;
  duration: string;
  price: number;
}

interface DemoExample {
  category: string;
  name: string;
  symbol: string;
  image: string;
  description: string;
  services: DemoService[];
}

const EXAMPLES: DemoExample[] = [
  {
    category: 'Hair & beauty',
    name: 'Willow Hair Studio',
    symbol: 'W',
    image: '/industries/beauty-and-skin.svg',
    description: 'Cuts, color and beauty appointments.',
    services: [
      { name: 'Cut & style', duration: '60 min', price: 65 },
      { name: 'Color & finish', duration: '90 min', price: 120 },
      { name: 'Classic haircut', duration: '30 min', price: 35 },
    ],
  },
  {
    category: 'Home & repair',
    name: 'BrightFix Home Services',
    symbol: 'B',
    image: '/industries/home-and-trades.svg',
    description: 'Repairs, installs and service visits.',
    services: [
      { name: 'Service call', duration: '60 min', price: 95 },
      { name: 'Computer repair', duration: '60 min', price: 100 },
      { name: 'Install consultation', duration: '30 min', price: 45 },
    ],
  },
  {
    category: 'Photography',
    name: 'Golden Hour Studio',
    symbol: 'G',
    image: '/industries/photography-and-creative.svg',
    description: 'Sessions, consultations and creative projects.',
    services: [
      { name: 'Portrait session', duration: '60 min', price: 180 },
      { name: 'Family session', duration: '90 min', price: 250 },
      { name: 'Event consultation', duration: '30 min', price: 0 },
    ],
  },
  {
    category: 'Food & catering',
    name: 'Gather & Table',
    symbol: 'G',
    image: '/industries/food-and-catering.svg',
    description: 'Tastings, catering requests and event planning.',
    services: [
      { name: 'Menu consultation', duration: '30 min', price: 0 },
      { name: 'Private tasting', duration: '60 min', price: 85 },
      { name: 'Event planning', duration: '45 min', price: 50 },
    ],
  },
  {
    category: 'Professional services',
    name: 'Northstar Consulting',
    symbol: 'N',
    image: '/industries/professional-services.svg',
    description: 'Consultations, requests and client follow up.',
    services: [
      { name: 'Initial consultation', duration: '45 min', price: 75 },
      { name: 'Document review', duration: '30 min', price: 50 },
      { name: 'Project session', duration: '60 min', price: 125 },
    ],
  },
];

const STEPS: StepContent[] = [
  {
    title: 'Make it yours',
    copy: 'Add your services, availability and business details.',
    art: <ArtCustomize />,
  },
  {
    title: 'Share your link',
    copy: 'Use it on your website, social media or anywhere you talk to customers.',
    art: <ArtShare />,
  },
  {
    title: 'Start getting booked',
    copy: 'Customers can book, request or get in touch from one simple place.',
    art: <ArtCalendar />,
  },
];

const FEATURE_GROUPS: FeatureContent[] = [
  {
    tone: 'lavender',
    icon: <IconCalendar />,
    title: 'Online bookings',
    label: 'Book 24/7',
    copy: 'Give customers one simple place to choose a service and find an available time.',
  },
  {
    tone: 'rose',
    icon: <IconPeople />,
    title: 'Customer management',
    label: 'Keep context together',
    copy: 'Keep customer details, history, forms and the next appointment in one place.',
  },
  {
    tone: 'sky',
    icon: <IconChat />,
    title: 'Automated messages',
    label: 'Stay in touch',
    copy: 'Send confirmations, reminders and updates without adding more follow up to your day.',
  },
  {
    tone: 'mint',
    icon: <IconChart />,
    title: 'Insights & reports',
    label: 'See what is working',
    copy: 'Follow bookings, activity and customer trends as your business grows.',
  },
];

const INDUSTRY_LINKS = [
  {
    href: '/features/salons',
    image: '/industries/beauty-and-skin.svg',
    title: 'Hair & beauty',
    copy: 'Salons, barbers, nails and beauty services.',
  },
  {
    href: '/features/massage',
    image: '/industries/fitness-and-training.svg',
    title: 'Wellness & fitness',
    copy: 'Gyms, studios, coaches and wellness professionals.',
  },
  {
    href: '/features/cleaning-restoration',
    image: '/industries/home-and-trades.svg',
    title: 'Property & trades',
    copy: 'Cleaning, restoration, plumbing, electrical and repair — homes and commercial properties.',
  },
  {
    href: '/admin/signup',
    image: '/industries/professional-services.svg',
    title: 'Professional services',
    copy: 'Consultants, accounting, notary and advisory work.',
  },
  {
    href: '/features/photographers',
    image: '/industries/photography-and-creative.svg',
    title: 'Photography & creative',
    copy: 'Photographers, creatives and project based services.',
  },
  {
    href: '/admin/signup',
    image: '/industries/real-estate.svg',
    title: 'Real estate',
    copy: 'Agents, property services and consultations.',
  },
  {
    href: '/admin/signup',
    image: '/industries/pets.svg',
    title: 'Pet services',
    copy: 'Grooming, walking, care and training.',
  },
  {
    href: '/features/food-orders',
    image: '/industries/food-and-catering.svg',
    title: 'Food & catering',
    copy: 'Caterers, prepared food, tastings and custom orders.',
  },
  {
    href: '/admin/signup',
    image: '/industries/teaching-and-lessons.svg',
    title: 'Classes & events',
    copy: 'Lessons, workshops, classes and group sessions.',
  },
  {
    href: '/admin/signup',
    image: '/industries/something-else.svg',
    title: 'Something else',
    copy: 'Start with your business and shape the page around it.',
  },
];

function HeadsetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
      <rect x="3" y="12" width="4" height="7" rx="2" />
      <rect x="17" y="12" width="4" height="7" rx="2" />
      <path d="M17 19c0 1.1-.9 2-2 2h-3" />
    </svg>
  );
}

export function HomePage() {
  const [plans, setPlans] = useState<PlanPublic[] | null>(null);
  const [pricing, setPricing] = useState<PublicPricing | null>(null);
  const [error, setError] = useState(false);
  const [people, setPeople] = useState(1);
  const [industry, setIndustry] = useState(0);
  const [service, setService] = useState(0);
  const [time, setTime] = useState('');
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    let live = true;
    api<{ plans: PlanPublic[]; pricing?: PublicPricing }>('/plans')
      .then((r) => {
        if (live) {
          setPlans(r.plans);
          setPricing(r.pricing ?? null);
        }
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, []);

  const example = EXAMPLES[industry]!;
  const selected = example.services[service]!;
  const plan = plans?.find((p) => p.code === 'booking_flex');

  const chooseIndustry = (next: number) => {
    setIndustry(next);
    setService(0);
    setTime('');
    setFinished(false);
  };

  return (
    <div className="book-home">
      <header className="book-home-bar">
        <a className="home-brand-link" href="/" aria-label="Book with Assemble home">
          <Brand />
        </a>
        <nav aria-label="Main">
          <a className="home-nav-link" href="#how-it-works">How it works</a>
          <a className="home-nav-link" href="#pricing">Pricing</a>
          <div className="home-nav-tools">
            <Appearance />
            <Help context={{ surface: 'site', version: BUILD_VERSION, screen: '/' }} />
          </div>
          <a className="home-signin" href="/admin/login">Sign in</a>
          <a className="home-get-started" href="/admin/signup">Get started</a>
        </nav>
      </header>

      <main id="main">
        <section className="book-hero">
          <div className="book-hero-copy">
            <span className="book-eyebrow">BOOKINGS MADE SIMPLE</span>
            <h1>Your business.<br /><span>Your booking page.</span></h1>
            <p>Take bookings, requests and customer communication in one place so you can spend less time on admin and more time doing what you love.</p>
            <div className="home-actions">
              <a className="home-primary" href="/admin/signup">Get started for free <span aria-hidden="true">→</span></a>
              <a className="home-secondary" href="#example">See how it works</a>
            </div>
            <ul className="home-proof-list" aria-label="Getting started">
              <li>No credit card required</li>
              <li>Set up at your own pace</li>
              <li>Built for businesses like yours</li>
            </ul>
          </div>

          <div className="home-demo-wrap">
            <div className="demo-tabs demo-tabs--hero" aria-label="Example business type">
              {EXAMPLES.map((item, i) => (
                <button key={item.category} aria-pressed={industry === i} onClick={() => chooseIndustry(i)}>
                  <img src={item.image} alt="" aria-hidden="true" />
                  <span>{item.category}</span>
                </button>
              ))}
            </div>

            <div id="example" className="book-demo">
              <div className={`demo-cover demo-cover-${industry}`}>
                {industry === 0 ? (
                  <img src="/salon-hero.webp" alt="A stylist working with a customer" width="1672" height="941" />
                ) : (
                  <div className="demo-industry-art">
                    <img src={example.image} alt="" aria-hidden="true" />
                    <span>{example.description}</span>
                  </div>
                )}
                <span className="demo-label">Interactive example</span>
              </div>

              <div className="demo-body">
                <div className="demo-title">
                  <span className="demo-monogram">{example.symbol}</span>
                  <div>
                    <h2>{example.name}</h2>
                    <p>{example.description}</p>
                  </div>
                </div>

                <div className="demo-meta" aria-label="Example business details">
                  <span>Online booking</span>
                  <span>Open today</span>
                  <span>Shareable page</span>
                </div>

                {!finished ? (
                  <>
                    <div className="demo-services">
                      {example.services.map((item, i) => (
                        <button
                          key={item.name}
                          aria-pressed={service === i}
                          onClick={() => {
                            setService(i);
                            setTime('');
                          }}
                        >
                          <span>
                            <strong>{item.name}</strong>
                            <small>{item.duration}</small>
                          </span>
                          <span className="demo-service-end">
                            <span>{item.price === 0 ? 'Free' : formatPrice(item.price * 100)}</span>
                            <span className="demo-book-chip">Book</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="demo-book-panel">
                      <p className="home-small">Choose an example time for {selected.name}</p>
                      <div className="demo-times">
                        {['10:00 AM', '11:30 AM', '2:00 PM'].map((item) => (
                          <button key={item} aria-pressed={time === item} onClick={() => setTime(item)}>{item}</button>
                        ))}
                      </div>
                      <button className="home-primary demo-continue" disabled={!time} onClick={() => setFinished(true)}>Preview booking</button>
                    </div>
                  </>
                ) : (
                  <div className="demo-confirmation" aria-live="polite">
                    <span aria-hidden="true">✓</span>
                    <h3>That was easy.</h3>
                    <p>{selected.name} · {time}</p>
                    <p>This is an example. No appointment was created.</p>
                    <button className="home-secondary" onClick={() => setFinished(false)}>Try another service</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="home-industries" aria-labelledby="industries-title">
          <div className="home-industries-heading">
            <span className="book-eyebrow">FOR EVERY KIND OF BUSINESS</span>
            <h2 id="industries-title">What kind of business do you run?</h2>
            <p>Book with Assemble works for all kinds of businesses. Here are a few.</p>
          </div>
          <div className="home-industry-grid">
            {INDUSTRY_LINKS.map((item) => (
              <a className="home-industry-card" key={item.title} href={item.href}>
                <span className="home-industry-icon" aria-hidden="true">
                  <img src={item.image} alt="" loading="lazy" decoding="async" />
                </span>
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.copy}</small>
                </span>
              </a>
            ))}
          </div>
        </section>

        <section className="home-how" id="how-it-works">
          <div className="home-section-intro home-section-intro--simple">
            <div>
              <span className="book-eyebrow">GET STARTED IN MINUTES</span>
              <h2>Three simple steps. One easier day.</h2>
              <p className="home-section-lede">Get your booking page up and running without adding another complicated system to your day.</p>
            </div>
          </div>
          <StepFlow steps={STEPS} />
          <div className="home-how-cta">
            <a className="home-primary" href="/admin/signup">Get started today <span aria-hidden="true">→</span></a>
            <span>No credit card required · 14 day free trial</span>
          </div>
        </section>

        <section className="home-capabilities" aria-labelledby="capabilities-title">
          <div className="home-capabilities-heading">
            <div>
              <span className="book-eyebrow">RUN YOUR BUSINESS, YOUR WAY</span>
              <h2 id="capabilities-title">The whole day, in one place.</h2>
            </div>
            <p>More than just bookings. Keep the customer, the appointment and the follow up together.</p>
          </div>
          <div className="home-capability-grid">
            {FEATURE_GROUPS.map((feature) => <FeatureCard key={feature.title} {...feature} />)}
          </div>
        </section>

        <aside className="home-setup-help">
          <span className="home-help-icon" aria-hidden="true"><HeadsetIcon /></span>
          <div>
            <span className="book-eyebrow">WANT A HAND GETTING STARTED?</span>
            <h2>We're here to help.</h2>
            <p>Get help setting up your services, hours and booking page, plus tips for getting the most out of Book with Assemble.</p>
          </div>
          <a className="home-secondary" href="/support">Get help & support <span aria-hidden="true">→</span></a>
        </aside>

        <section className="home-pricing" id="pricing">
          <div className="home-pricing-copy">
            <span className="book-eyebrow">SIMPLE, TRANSPARENT PRICING</span>
            <h2>Room to grow.<br />Without the guesswork.</h2>
            {plan ? (
              <>
                <p className="home-base-price">{formatPrice(plan.monthly_price_cents, plan.currency)}<small> / month</small></p>
                <p>Everything you need to get started, with one bookable professional included.</p>
              </>
            ) : (
              <p role={error ? 'alert' : undefined}>{error ? 'Prices could not load. Please refresh.' : 'Loading prices…'}</p>
            )}
            <ul className="home-price-list home-price-list--simple">
              <li>Your first professional is included</li>
              <li>{formatPrice(plan?.entitlements.extraProviderPriceCents ?? 700)} a month for each additional professional</li>
              <li>Transactional email included for booking updates</li>
              <li>{pricing?.included_sms_credits ?? 300} text credits included every billing cycle</li>
              <li>{pricing?.credit_package_credits ?? 500} more credits for {formatPrice(pricing?.credit_package_price_cents ?? 1000)} when needed</li>
              <li>Optional marketing messages: {formatPrice(pricing?.marketing_price_cents ?? 2000)} a month</li>
              <li>No booking commission</li>
            </ul>
          </div>

          {plan && (
            <div className="price-calculator">
              <label className="book-eyebrow" htmlFor="professional-count">How many bookable professionals?</label>
              <div className="home-stepper">
                <button aria-label="Fewer professionals" disabled={people === 1} onClick={() => setPeople((n) => n - 1)}>−</button>
                <input
                  id="professional-count"
                  type="number"
                  min={1}
                  max={500}
                  value={people}
                  onChange={(e) => setPeople(Math.max(1, Math.min(500, Math.trunc(Number(e.target.value)) || 1)))}
                />
                <button aria-label="More professionals" disabled={people === 500} onClick={() => setPeople((n) => n + 1)}>+</button>
              </div>
              <output aria-live="polite">{formatPrice(monthlyTotal(plan.monthly_price_cents, plan.entitlements.extraProviderPriceCents ?? 0, people), plan.currency)}<small> / month</small></output>
              <p className="home-small">1 professional is included. Each additional professional is {formatPrice(plan.entitlements.extraProviderPriceCents ?? 0, plan.currency)} per month.</p>
              <a className="home-primary" href={`/admin/signup?professionals=${people}`}>Get started for {formatPrice(monthlyTotal(plan.monthly_price_cents, plan.entitlements.extraProviderPriceCents ?? 0, people), plan.currency)} / month</a>
              <p className="home-small">No credit card required for the 14 day trial.</p>
            </div>
          )}
        </section>
      </main>

      <footer className="book-home-footer">
        <Brand />
        <span>Built for the way you work.</span>
        <a href="/admin/login">Business sign in</a>
        <a href="/support">Contact</a>
        <LegalFooter compact />
      </footer>
    </div>
  );
}
